"use client";
import { useState, useEffect } from "react";
import axios from "axios";

const API = "https://erp-xe9u.onrender.com/api";

const cls = (...c) => c.filter(Boolean).join(" ");

const ACTION_STYLE = {
  created: "bg-blue-900/40 text-blue-300 border-blue-700/40",
  updated: "bg-gray-700/60 text-gray-300 border-gray-600",
  deleted: "bg-red-900/40 text-red-300 border-red-700/40",
  email_sent: "bg-purple-900/40 text-purple-300 border-purple-700/40",
  rejected: "bg-red-900/40 text-red-300 border-red-700/40",
  payment_updated: "bg-emerald-900/40 text-emerald-300 border-emerald-700/40",
  approved: "bg-green-900/40 text-green-300 border-green-700/40",
  delivered: "bg-green-900/40 text-green-300 border-green-700/40",
  partially_delivered: "bg-orange-900/40 text-orange-300 border-orange-700/40",
  voided: "bg-red-900/40 text-red-300 border-red-700/40",
};

const ACTION_LABEL = {
  created: "Created",
  updated: "Updated",
  deleted: "Deleted",
  email_sent: "Emailed",
  rejected: "Rejected",
  payment_updated: "Payment Updated",
  approved: "Approved",
  delivered: "Delivered",
  partially_delivered: "Partial Delivery",
  voided: "Voided",
};

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function MaterialOrderTimeline({ orderId, token, refreshKey }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    setError("");
    axios
      .get(`${API}/entity-timeline/materialOrder/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setEvents(r.data.data || []))
      .catch((err) => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, [orderId, token, refreshKey]);

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <button
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center justify-between px-5 py-3 bg-gray-900 hover:bg-gray-850 transition-colors text-left"
      >
        <h2 className="text-sm font-semibold text-white">
          Timeline
          {events.length > 0 && (
            <span className="ml-2 text-xs text-gray-500 font-normal">
              ({events.length} event{events.length !== 1 ? "s" : ""})
            </span>
          )}
        </h2>
        <svg
          className={`w-3.5 h-3.5 text-gray-500 transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {expanded && (
        <div className="p-5">
          {loading && (
            <p className="text-xs text-gray-500 flex items-center gap-2">
              <span className="inline-block w-3 h-3 border border-gray-600 border-t-teal-500 rounded-full animate-spin" />
              Loading timeline…
            </p>
          )}

          {!loading && error && <p className="text-xs text-red-400">{error}</p>}

          {!loading && !error && events.length === 0 && (
            <p className="text-xs text-gray-500">No activity recorded yet.</p>
          )}

          {!loading && !error && events.length > 0 && (
            <ol className="space-y-0">
              {events.map((ev, i) => (
                <li key={ev.id || i} className="relative pl-6 pb-4 last:pb-0">
                  {i < events.length - 1 && (
                    <span className="absolute left-[5px] top-3 bottom-0 w-px bg-gray-700" />
                  )}
                  <span className="absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full bg-gray-600 border-2 border-gray-800" />

                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={cls(
                            "px-1.5 py-0.5 text-[9px] font-semibold rounded border uppercase tracking-wide",
                            ACTION_STYLE[ev.action] ||
                              "bg-gray-700/60 text-gray-300 border-gray-600",
                          )}
                        >
                          {ACTION_LABEL[ev.action] || ev.action}
                        </span>
                        <span className="text-[10px] text-gray-500">
                          {timeAgo(ev.timestamp)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-200 mt-1">
                        {ev.description}
                      </p>
                    </div>
                    <span className="text-[10px] text-gray-500 flex-shrink-0 whitespace-nowrap">
                      {ev.changedBy || "Unknown"}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
